import {
  BehaviorHandler,
  MissionBehaviors,
  MissionBehaviorsFactory,
} from './types'

const chainHandlers =
  (handlers: readonly BehaviorHandler[]): BehaviorHandler =>
  async (ctx) => {
    for (const handler of handlers) {
      if (ctx.signal.aborted) {
        return
      }
      await handler(ctx)
    }
  }

const mergeHandlerMaps = (
  maps: ReadonlyArray<ReadonlyMap<string, BehaviorHandler> | undefined>,
): ReadonlyMap<string, BehaviorHandler> | undefined => {
  const grouped = new Map<string, BehaviorHandler[]>()
  for (const map of maps) {
    for (const [key, handler] of map ?? []) {
      const handlers = grouped.get(key)
      if (handlers) {
        handlers.push(handler)
      } else {
        grouped.set(key, [handler])
      }
    }
  }
  if (grouped.size === 0) {
    return undefined
  }
  return new Map(
    [...grouped].map(([key, handlers]) => [
      key,
      handlers.length === 1 ? handlers[0] : chainHandlers(handlers),
    ]),
  )
}

const mergeUniqueStrings = (
  values: ReadonlyArray<ReadonlyArray<string> | undefined>,
): ReadonlyArray<string> | undefined => {
  const merged: string[] = []
  const seen = new Set<string>()
  for (const list of values) {
    for (const value of list ?? []) {
      if (!seen.has(value)) {
        seen.add(value)
        merged.push(value)
      }
    }
  }
  return merged.length > 0 ? merged : undefined
}

export const composeBehaviorFactories = (
  factories: ReadonlyArray<MissionBehaviorsFactory | undefined>,
): MissionBehaviorsFactory | undefined => {
  const active = factories.filter(
    (factory): factory is MissionBehaviorsFactory => factory !== undefined,
  )
  if (active.length === 0) {
    return undefined
  }
  if (active.length === 1) {
    return active[0]
  }
  return (): MissionBehaviors => {
    const behaviors = active.map((factory) => factory())
    const onPlay = behaviors.flatMap((behavior) => behavior.onPlay ?? [])
    return {
      onPlay: onPlay.length > 0 ? onPlay : undefined,
      onTouchCharacter: mergeHandlerMaps(
        behaviors.map((behavior) => behavior.onTouchCharacter),
      ),
      onMessage: mergeHandlerMaps(
        behaviors.map((behavior) => behavior.onMessage),
      ),
      onClick: mergeHandlerMaps(behaviors.map((behavior) => behavior.onClick)),
      serverStorageTemplates: mergeUniqueStrings(
        behaviors.map((behavior) => behavior.serverStorageTemplates),
      ),
    }
  }
}
